import React, { useEffect, useState } from "react";
import { Loader2, Download, ArrowDownCircle, ArrowUpCircle, Scale } from "lucide-react";
import api, { money, openPdf } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const totalsStr = (t) => { const e = Object.entries(t || {}); return e.length ? e.map(([c, v]) => money(c, v)).join(" · ") : money("GTQ", 0); };
const isZero = (t) => { const vals = Object.values(t || {}); return vals.length === 0 || vals.every((v) => !v); };

function Line({ label, map }) {
  const zero = isZero(map);
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/60 text-sm" data-testid={`cf-line-${label}`}>
      <span className={zero ? "text-muted-foreground/50" : "text-muted-foreground"}>{label}</span>
      <span className={`font-mono ${zero ? "text-muted-foreground/40" : ""}`}>{totalsStr(map)}</span>
    </div>
  );
}

export default function CashFlow() {
  const now = new Date();
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (p) => {
    setLoading(true);
    api.get(`/finance/cashflow?period=${p}`).then((r) => setData(r.data)).catch((e) => toast.error("No se pudo cargar el flujo de caja")).finally(() => setLoading(false));
  };
  useEffect(() => { load(period); }, [period]);

  const exportPdf = async () => {
    try { await openPdf(`/finance/cashflow/pdf?period=${period}`); } catch { toast.error("No se pudo generar el PDF"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="border-l-[3px] border-primary pl-4">
          <h2 className="font-heading text-xl font-bold">Flujo de Caja</h2>
          <p className="text-sm text-muted-foreground">Ingresos y egresos reales del periodo</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-sm h-9 w-44 text-xs" data-testid="cf-period" />
          <Button size="sm" variant="outline" className="rounded-sm h-9 text-xs font-bold gap-1.5" onClick={exportPdf} data-testid="cf-export-pdf"><Download className="h-3.5 w-3.5" /> PDF</Button>
        </div>
      </div>

      {loading || !data ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="rounded-sm border shadow-none p-5 border-t-4 border-t-green-500" data-testid="cf-income">
              <h3 className="font-heading font-bold flex items-center gap-2 mb-3 text-green-700"><ArrowDownCircle className="h-5 w-5" /> Ingresos</h3>
              <Line label="Cobros a clientes" map={data.income.cobros} />
              <Line label="Otros ingresos (tesorería)" map={data.income.otros} />
              <div className="flex items-center justify-between pt-3 font-bold text-base text-green-700" data-testid="cf-income-total">
                <span>Total ingresos</span><span className="font-mono">{totalsStr(data.income.total)}</span>
              </div>
            </Card>

            <Card className="rounded-sm border shadow-none p-5 border-t-4 border-t-red-500" data-testid="cf-expense">
              <h3 className="font-heading font-bold flex items-center gap-2 mb-3 text-red-700"><ArrowUpCircle className="h-5 w-5" /> Egresos</h3>
              <Line label="Pagos a proveedores" map={data.expense.pagos} />
              <Line label="Gastos de caja chica" map={data.expense.caja_chica} />
              <Line label="Otros egresos (tesorería)" map={data.expense.otros} />
              <div className="flex items-center justify-between pt-3 font-bold text-base text-red-700" data-testid="cf-expense-total">
                <span>Total egresos</span><span className="font-mono">{totalsStr(data.expense.total)}</span>
              </div>
            </Card>
          </div>

          <Card className="rounded-sm border-none shadow-none p-6 bg-primary text-primary-foreground" data-testid="cf-net">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-heading font-bold uppercase tracking-widest text-sm"><Scale className="h-5 w-5" /> Flujo neto del periodo</div>
              <div className="text-2xl font-heading font-black font-mono">{totalsStr(data.net)}</div>
            </div>
          </Card>

          <p className="text-xs text-muted-foreground">Nota: el flujo de caja considera cobros, pagos a proveedores, gastos de caja chica y movimientos manuales de tesorería. Se excluyen los movimientos bancarios automáticos ligados a recibos/pagos y las reposiciones de caja chica para evitar doble conteo.</p>
        </>
      )}
    </div>
  );
}
